import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Button, InputNumber, Input, Empty, Typography, Divider, message, Grid, Card, Space, Alert, Tag, Segmented, Select, Checkbox,
} from 'antd';
import { DeleteOutlined, ArrowLeftOutlined, TagOutlined, ShopOutlined, HomeOutlined } from '@ant-design/icons';
import { useCart, customSpecLabel, keyOf } from '../context/CartContext';
import { useCustomer } from '../context/CustomerContext';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

// 運費：宅配 100 / 超商店到店 60
const SHIPPING_CVS = 60;
const SHIPPING_HOME = 100;

const BRAND = 'var(--brand-primary, #222957)';
const PANEL = '#F9FAFD';
const BORDER = '#E5E7F5';
const HEADING = '#222A5A';

// 客製手鏈可編輯的規格選項
const CHAIN_OPTIONS = [
  { label: '彈性繩', value: 'elastic' },
  { label: '調節鍊', value: 'adjustable' },
  { label: '磁扣款', value: 'magnetic' },
];
const CIRCLE_OPTIONS = [
  { label: '單圈', value: 'single' },
  { label: '雙圈', value: 'double' },
];
const WRIST_OPTIONS = [];
for (let i = 13; i <= 22; i += 0.5) WRIST_OPTIONS.push({ value: i, label: `${i} cm` });

// custom_detail 內文字對照（唯讀摘要用）
const METHOD_LABEL = { wuxing: '五行分析', numerology: '生命靈數', effect: '心願功效' };
const lineKey = keyOf; // 用 context 的 keyOf（客製品項以 custom_uid 為唯一鍵，避免多筆合併）

export default function CartPage() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const { items, remove, removeByKey, updateQty, updateCustom, updateVariant, subtotal, clear } = useCart();
  const { isLoggedIn, login } = useCustomer();

  const [code, setCode] = useState('');
  const [coupon, setCoupon] = useState(null); // {valid, discount, final_amount, description, reason}
  const [applying, setApplying] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [pricing, setPricing] = useState(null); // 客製手鏈價格參數（/api/settings/pricing）
  const [expanded, setExpanded] = useState({}); // 品項展開修改狀態（key -> bool）
  const [variantsByProduct, setVariantsByProduct] = useState({}); // productId -> 變體陣列（設計款換規格用）

  // 展開設計款品項時載入該商品的變體清單（快取）
  const loadVariants = useCallback((productId) => {
    if (productId == null || variantsByProduct[productId] !== undefined) return;
    setVariantsByProduct((m) => ({ ...m, [productId]: null })); // null = 載入中
    fetch(`${API_BASE}/api/variants/public/products/${productId}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setVariantsByProduct((m) => ({ ...m, [productId]: Array.isArray(list) ? list : [] })))
      .catch(() => setVariantsByProduct((m) => ({ ...m, [productId]: [] })));
  }, [variantsByProduct]);

  const toggleExpand = (key, productId, isCustom) => {
    setExpanded((e) => ({ ...e, [key]: !e[key] }));
    if (!isCustom && productId != null) loadVariants(productId);
  };

  // 配送方式：'cvs' 超商取貨 / 'home' 宅配到府
  const [logisticsType, setLogisticsType] = useState('cvs');
  // 宅配收件資訊
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');

  // 會員已儲存的收件資料（登入時載入，選物流可一鍵帶入）
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [prefilled, setPrefilled] = useState(false); // 是否已自動帶入預設，避免蓋掉使用者編輯
  const [saveNewAddr, setSaveNewAddr] = useState(false); // 結帳時把本次宅配存進收件資料
  // 超商：選定門市 + 取件人（可從通訊錄帶入或綠界地圖選店）
  const [cvsStore, setCvsStore] = useState(null); // {cvs_subtype, cvs_store_id, cvs_store_name, cvs_store_address}
  const [cvsName, setCvsName] = useState('');
  const [cvsPhone, setCvsPhone] = useState('');

  const savedHome = savedAddresses.filter((a) => a.logistics_type === 'home');
  const savedCvs = savedAddresses.filter((a) => a.logistics_type === 'cvs');

  const isHome = logisticsType === 'home';
  const shipping = isHome ? SHIPPING_HOME : SHIPPING_CVS;

  // 載入會員收件資料（登入確認後才載，確保登入導回 /cart 也會抓到）
  useEffect(() => {
    if (!isLoggedIn) return;
    const token = (() => { try { return localStorage.getItem('customer_token'); } catch { return null; } })();
    if (!token) return;
    fetch(`${API_BASE}/api/customer/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setSavedAddresses(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [isLoggedIn]);

  // 綠界電子地圖選店導回 /cart?cvs=1&... → 帶入門市並切到超商
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('cvs') === '1' && q.get('storeId')) {
      setCvsStore({
        cvs_subtype: q.get('subType') || 'UNIMARTC2C',
        cvs_store_id: q.get('storeId') || '',
        cvs_store_name: q.get('storeName') || '',
        cvs_store_address: q.get('storeAddr') || '',
      });
      setLogisticsType('cvs');
      ['cvs', 'subType', 'storeId', 'storeName', 'storeAddr'].forEach((k) => q.delete(k));
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  // 從通訊錄帶入一筆超商門市
  const applyCvsAddress = useCallback((a) => {
    if (!a) return;
    setCvsStore({
      cvs_subtype: a.cvs_subtype, cvs_store_id: a.cvs_store_id,
      cvs_store_name: a.cvs_store_name, cvs_store_address: a.cvs_store_address,
    });
    setCvsName(a.recipient_name || '');
    setCvsPhone(a.recipient_phone || '');
  }, []);

  const pickCvsStore = useCallback(() => {
    window.location.href = `${API_BASE}/api/logistics/ecpay/map?sub_type=${cvsStore?.cvs_subtype || 'UNIMARTC2C'}&back=cart`;
  }, [cvsStore]);

  // 套用一筆收件資料到宅配欄位
  const applyAddress = useCallback((a) => {
    if (!a) return;
    setRecipientName(a.recipient_name || '');
    setRecipientPhone(a.recipient_phone || '');
    setRecipientAddress(a.recipient_address || '');
  }, []);

  // 選宅配且欄位為空時，自動帶入預設收件資料（只做一次）
  useEffect(() => {
    if (!isHome || prefilled || savedHome.length === 0) return;
    if (recipientName || recipientPhone || recipientAddress) return;
    const def = savedHome.find((a) => a.is_default) || savedHome[0];
    applyAddress(def);
    setPrefilled(true);
  }, [isHome, prefilled, savedHome, recipientName, recipientPhone, recipientAddress, applyAddress]);

  // 只有購物車含客製品項時才需抓價格參數（重算客製價用）
  const hasCustom = items.some((it) => it.is_custom);
  useEffect(() => {
    if (!hasCustom || pricing) return;
    fetch(`${API_BASE}/api/settings/pricing`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setPricing(d); })
      .catch(() => {});
  }, [hasCustom, pricing]);

  // 會員自己收藏的優惠券（供下拉挑選）
  const [myCoupons, setMyCoupons] = useState([]);
  useEffect(() => {
    const token = (() => { try { return localStorage.getItem('customer_token'); } catch { return null; } })();
    if (!token) return;
    fetch(`${API_BASE}/api/coupons/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setMyCoupons(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // 套用優惠碼；可傳入 overrideCode（下拉選擇時）覆蓋輸入框
  const applyCoupon = useCallback(async (overrideCode) => {
    const useCode = (typeof overrideCode === 'string' ? overrideCode : code).trim();
    if (!useCode) return;
    setApplying(true);
    try {
      const res = await fetch(`${API_BASE}/api/coupons/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: useCode, subtotal, shipping }),
      });
      const data = await res.json();
      setCoupon(data);
      if (data.valid) { setCode(useCode); message.success('優惠碼已套用'); }
      else message.warning(data.reason || '優惠碼無法使用');
    } catch {
      message.error('套用優惠碼失敗，請稍後再試');
      setCoupon(null);
    } finally {
      setApplying(false);
    }
  }, [code, subtotal, shipping]);

  // 優惠券顯示文字與「是否可用於目前購物車」
  const couponLabelText = (c) => {
    if (c.type === 'percentage') return `${c.percent || 0}% 折扣`;
    if (c.type === 'fixed') return `折 NT$ ${c.amount || 0}`;
    if (c.type === 'threshold') return `滿 ${c.min_spend || 0} 折 ${c.amount || 0}`;
    if (c.type === 'free_shipping') return '免運費';
    if (c.type === 'first_order') return `首單折 NT$ ${c.amount || 0}`;
    return c.description || '優惠券';
  };
  const couponDisabledReason = (c) => {
    if (c.used) return '已使用';
    if (c.expired) return '已過期';
    if (c.not_started) return '尚未開始';
    if (c.sold_out) return '已領完';
    if (c.min_spend && subtotal < c.min_spend) return `需滿 NT$ ${c.min_spend}`;
    return null;
  };

  const discount = coupon?.valid ? (coupon.discount || 0) : 0;
  const finalAmount = coupon?.valid && coupon.final_amount != null
    ? coupon.final_amount
    : subtotal + shipping - discount;

  // 結帳：購物車一次結多件，建立一張多品項訂單並導向綠界
  const handleCheckout = useCallback(async () => {
    if (items.length === 0) return;

    // 宅配需填齊收件資訊
    if (isHome) {
      if (!recipientName.trim() || !recipientPhone.trim() || !recipientAddress.trim()) {
        message.warning('請填寫宅配收件人姓名、電話與地址');
        return;
      }
    }

    // 需以 Google 登入（沿用客製流程的 customer_token）
    const token = (() => { try { return localStorage.getItem('customer_token'); } catch { return null; } })();
    if (!token) {
      message.warning('請先登入以完成結帳，登入後會回到購物車');
      login('/cart'); // 導向 Google 登入，回來仍是 /cart，CustomerProvider 會接住 ctoken
      return;
    }

    setCheckingOut(true);
    try {
      // 若勾選，把本次宅配收件資料存進會員通訊錄（best-effort，不影響結帳）
      if (isHome && saveNewAddr) {
        fetch(`${API_BASE}/api/customer/addresses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            logistics_type: 'home',
            recipient_name: recipientName.trim(),
            recipient_phone: recipientPhone.trim(),
            recipient_address: recipientAddress.trim(),
          }),
        }).catch(() => {});
      }
      // 送出全部購物車品項；金額由伺服器端重算（不信任前端價格）
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id != null ? it.product_id : it.odoo_product_id,
          variant_id: it.variant_id || null,
          name: it.name,
          price: it.price || 0,
          qty: it.qty || 1,
          spec_label: it.spec_label || null,
          image_url: it.image_url || null,
          // 客製品項：帶旗標與完整設計資料，後端據此權威重算價格
          ...(it.is_custom ? { is_custom: true, custom_detail: it.custom_detail || {} } : {}),
        })),
        coupon_code: coupon?.valid ? code.trim() : null,
        // 配送方式 + 對應欄位（宅配帶收件人；超商由綠界電子地圖選店後回填 cvs_store_*）
        logistics_type: logisticsType,
        ...(isHome
          ? {
              recipient_name: recipientName.trim(),
              recipient_phone: recipientPhone.trim(),
              recipient_address: recipientAddress.trim(),
            }
          : {
              // 超商：若已選門市/填取件人則一併帶上（未選則付款頁再選店）
              ...(cvsStore ? {
                cvs_store_id: cvsStore.cvs_store_id,
                cvs_store_name: cvsStore.cvs_store_name,
                cvs_store_address: cvsStore.cvs_store_address,
              } : {}),
              ...(cvsName.trim() ? { recipient_name: cvsName.trim() } : {}),
              ...(cvsPhone.trim() ? { recipient_phone: cvsPhone.trim() } : {}),
            }),
      };
      const res = await fetch(`${API_BASE}/api/orders/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 庫存不足 / 其他錯誤：顯示後端訊息
        message.error(data.detail || '結帳失敗，請稍後再試');
        setCheckingOut(false);
        return;
      }

      // 清空購物車後導向綠界付款（伺服器回傳自動送出表單）
      clear();
      window.location.href = `${API_BASE}/api/payments/ecpay/checkout/${data.id}?ctoken=${encodeURIComponent(localStorage.getItem('customer_token') || '')}`;
    } catch {
      message.error('結帳失敗，請稍後再試');
      setCheckingOut(false);
    }
  }, [items, clear, coupon, code, logisticsType, isHome, recipientName, recipientPhone, recipientAddress, saveNewAddr, cvsStore, cvsName, cvsPhone, login]);

  const containerStyle = {
    maxWidth: isMobile ? 600 : 1160,
    margin: '0 auto',
    padding: isMobile ? '16px' : '28px 32px 48px',
  };

  // 客製手鏈品項：可就地編輯 鏈形／圈數／手圍（改動即重算價格 + spec_label），
  // 下方唯讀顯示命理設計摘要（五行/靈數/功效 + 已選顏色）
  const renderCustomItem = (it) => {
    const key = lineKey(it);
    const d = it.custom_detail || {};
    const patch = (p) => updateCustom(key, p, pricing);
    const colors = Array.isArray(d.selected_colors) ? d.selected_colors : [];
    const crystals = Array.isArray(d.selected_crystals) ? d.selected_crystals : [];
    const isOpen = !!expanded[key];
    return (
      <Card key={it.uid || key} size="small" style={{ marginBottom: 12, borderRadius: 14, border: `1px solid ${BORDER}` }} styles={{ body: { padding: 14 } }}>
        {/* 收合列：名稱 + 規格摘要 + 價格 + 展開/移除 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <Space size={6} wrap>
              <Text strong>{it.name}</Text>
              <Tag color="geekblue">客製</Tag>
            </Space>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{customSpecLabel(d)}</Text></div>
            <div><Text style={{ color: BRAND, fontWeight: 700 }}>NT$ {it.price}</Text></div>
          </div>
          <Space direction="vertical" align="end" size={4}>
            <Button type="link" size="small" onClick={() => toggleExpand(key)}>
              {isOpen ? '收合 ▴' : '修改 ▾'}
            </Button>
            <Button type="text" danger size="small" icon={<DeleteOutlined />}
              onClick={() => removeByKey(key)}>移除</Button>
          </Space>
        </div>

        {isOpen && (
          <>
            {/* 可編輯規格：客製品項只能改 鏈形／圈數／手圍 */}
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>鏈形</Text>
              <Segmented className="brand-segmented" block size="small" value={d.chain_style || 'adjustable'}
                options={CHAIN_OPTIONS} onChange={(v) => patch({ chain_style: v })} />
            </div>
            <div style={{ marginTop: 10 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>圈數</Text>
              <Segmented className="brand-segmented" block size="small" value={d.circle_design || 'single'}
                options={CIRCLE_OPTIONS} onChange={(v) => patch({ circle_design: v })} />
            </div>
            <div style={{ marginTop: 10 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>手圍</Text>
              <Select size="small" style={{ width: 140 }} placeholder="選擇手圍"
                value={d.wrist_size ?? undefined} options={WRIST_OPTIONS}
                onChange={(v) => patch({ wrist_size: v })} />
            </div>

            {/* 唯讀設計摘要（配色/水晶不可就地改） */}
            <Divider style={{ margin: '12px 0 8px' }} />
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {d.color_method && (
                <div><Text type="secondary" style={{ fontSize: 12 }}>配色方式：</Text><Text style={{ fontSize: 12 }}>{METHOD_LABEL[d.color_method] || d.color_method}{d.effect_choice ? `（${d.effect_choice}）` : ''}</Text></div>
              )}
              {colors.length > 0 && (
                <div><Text type="secondary" style={{ fontSize: 12 }}>選擇顏色：</Text>{colors.map((c) => <Tag key={c} style={{ fontSize: 11, marginInlineEnd: 4 }}>{c}</Tag>)}</div>
              )}
              {crystals.length > 0 && (
                <div><Text type="secondary" style={{ fontSize: 12 }}>客製水晶：</Text><Text style={{ fontSize: 12 }}>{crystals.map((c) => c.crystal_name || c.name).filter(Boolean).join('、')}</Text></div>
              )}
              {d.special_requirements && (
                <div><Text type="secondary" style={{ fontSize: 12 }}>特殊需求：</Text><Text style={{ fontSize: 12 }}>{d.special_requirements}</Text></div>
              )}
            </Space>

            {/* 配色要改 → 需重新走客製流程 */}
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="想更改配色 / 水晶？"
              description="配色與選石是命理設計的一部分，無法就地修改；請重新走一次客製流程。"
              action={
                <Button size="small" type="primary" onClick={() => navigate('/custom')}>
                  重新客製
                </Button>
              }
            />
          </>
        )}
      </Card>
    );
  };

  // 一般商品品項卡（設計款）：可展開換購買規格（變體）
  const renderPlainItem = (it) => {
    const thumb = isMobile ? 72 : 104;
    const pid = it.product_id != null ? it.product_id : it.odoo_product_id;
    const key = lineKey(it);
    const isOpen = !!expanded[key];
    const variants = variantsByProduct[pid]; // undefined=未載入, null=載入中, []=無
    return (
      <Card key={it.uid || key} size="small" className="cart-line-card" style={{ marginBottom: 14, borderRadius: 14, border: `1px solid ${BORDER}` }} styles={{ body: { padding: isMobile ? 14 : 18 } }}>
        <div style={{ display: 'flex', gap: isMobile ? 12 : 18, alignItems: 'center' }}>
          {it.image_url ? (
            <img src={it.image_url} alt={it.name}
              style={{ width: thumb, height: thumb, objectFit: 'cover', borderRadius: 12, flex: '0 0 auto', background: PANEL }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div style={{ width: thumb, height: thumb, borderRadius: 12, flex: '0 0 auto', background: 'linear-gradient(135deg, #E5E7F5, #e8eaf6)' }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong ellipsis style={{ display: 'block', color: HEADING, fontSize: isMobile ? 14 : 16 }}>{it.name}</Text>
            {it.spec_label ? <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{it.spec_label}</Text> : null}
            <Text style={{ color: BRAND, fontWeight: 700, fontSize: isMobile ? 15 : 17 }}>NT$ {it.price}</Text>
            <div>
              <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
                onClick={() => toggleExpand(key, pid, false)}>
                {isOpen ? '收合 ▴' : '修改規格 ▾'}
              </Button>
            </div>
          </div>
          <Space direction="vertical" align="end" size={6}>
            <InputNumber
              min={1}
              value={it.qty}
              onChange={(v) => updateQty(pid, it.variant_id || null, v)}
              style={{ width: isMobile ? 76 : 88 }}
            />
            <Button type="text" danger size="small" icon={<DeleteOutlined />}
              onClick={() => remove(pid, it.variant_id || null)}>移除</Button>
          </Space>
        </div>

        {isOpen && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #eee' }}>
            {variants == null ? (
              <Text type="secondary" style={{ fontSize: 12 }}>載入規格中…</Text>
            ) : variants.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>此商品沒有其他規格可選。</Text>
            ) : (() => {
              // 規格維度（如 手圍 / 款式），與客製一致用 Segmented 呈現
              const specKeys = [];
              variants.forEach((v) => Object.keys(v.spec || {}).forEach((k) => { if (!specKeys.includes(k)) specKeys.push(k); }));
              const cur = (variants.find((v) => v.id === it.variant_id) || {}).spec || {};
              const applyVariant = (v) => {
                // 換規格會改變 variant_id → line key 也會變；把展開狀態搬到新 key，
                // 讓使用者調整手圍／款式後維持展開（沒點「收合」就不收起來）
                const newKey = lineKey({ ...it, variant_id: v.id });
                if (newKey !== key) {
                  setExpanded((e) => { const n = { ...e, [newKey]: true }; delete n[key]; return n; });
                }
                updateVariant(pid, it.variant_id || null, {
                  variant_id: v.id, spec_label: v.spec_label || '', price: v.price != null ? v.price : it.price,
                });
              };
              const pick = (key, val) => {
                const cands = variants.filter((v) => (v.spec || {})[key] === val);
                if (!cands.length) return;
                const inStock = cands.filter((v) => (v.stock || 0) > 0);
                const pool = inStock.length ? inStock : cands;
                let best = pool[0]; let bestScore = -1;
                pool.forEach((v) => {
                  let s = 0; specKeys.forEach((k) => { if (k !== key && (v.spec || {})[k] === cur[k]) s += 1; });
                  if (s > bestScore) { bestScore = s; best = v; }
                });
                applyVariant(best);
              };
              // 無規格維度（單一屬性變體）→ 直接列變體
              if (specKeys.length === 0) {
                return (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>選擇規格</Text>
                    <Segmented className="brand-segmented" block size="small" value={it.variant_id}
                      options={variants.map((v) => ({ label: v.spec_label || '規格', value: v.id, disabled: (v.stock || 0) <= 0 && v.id !== it.variant_id }))}
                      onChange={(vid) => { const v = variants.find((x) => x.id === vid); if (v) applyVariant(v); }} />
                  </div>
                );
              }
              return specKeys.map((key) => {
                const opts = [];
                variants.forEach((v) => { const val = (v.spec || {})[key]; if (val != null && !opts.some((o) => o.value === val)) opts.push({ label: val, value: val }); });
                return (
                  <div key={key} style={{ marginBottom: 10 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{key}</Text>
                    <Segmented className="brand-segmented" block size="small" value={cur[key]} options={opts} onChange={(val) => pick(key, val)} />
                  </div>
                );
              });
            })()}
          </div>
        )}
      </Card>
    );
  };

  // 配送方式選擇（超商 / 宅配）
  const shippingSelector = (
    <Card size="small" style={{ marginBottom: 12, borderRadius: 14, border: `1px solid ${BORDER}` }} styles={{ body: { padding: 16 } }}>
      <Text strong style={{ display: 'block', marginBottom: 10, color: HEADING }}>配送方式</Text>
      <Segmented
        className="brand-segmented"
        block
        size="large"
        value={logisticsType}
        onChange={setLogisticsType}
        options={[
          { label: (<span><ShopOutlined /> 超商取貨（NT$ {SHIPPING_CVS}）</span>), value: 'cvs' },
          { label: (<span><HomeOutlined /> 宅配到府（NT$ {SHIPPING_HOME}）</span>), value: 'home' },
        ]}
      />

      {isHome ? (
        <div style={{ marginTop: 14 }}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {savedHome.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>從我的收件資料帶入</Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder="選擇已儲存的收件資料"
                  size={isMobile ? 'large' : 'middle'}
                  onChange={(id) => applyAddress(savedHome.find((a) => a.id === id))}
                  options={savedHome.map((a) => ({
                    value: a.id,
                    label: `${a.is_default ? '★ ' : ''}${a.recipient_name}／${a.recipient_phone}${a.label ? `（${a.label}）` : ''}`,
                  }))}
                />
              </div>
            )}
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>收件人姓名</Text>
              <Input
                placeholder="請輸入收件人姓名"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                style={isMobile ? { height: 44, fontSize: 16 } : undefined}
              />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>聯絡電話</Text>
              <Input
                placeholder="請輸入聯絡電話"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                inputMode="tel"
                style={isMobile ? { height: 44, fontSize: 16 } : undefined}
              />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>收件地址</Text>
              <Input.TextArea
                placeholder="請輸入完整收件地址（含縣市、郵遞區號）"
                value={recipientAddress}
                onChange={(e) => setRecipientAddress(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
                style={isMobile ? { fontSize: 16 } : undefined}
              />
            </div>
            <Checkbox checked={saveNewAddr} onChange={(e) => setSaveNewAddr(e.target.checked)}>
              <Text style={{ fontSize: 13 }}>儲存此收件資料，下次自動帶入</Text>
            </Checkbox>
          </Space>
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {savedCvs.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>從我的超商門市帶入</Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder="選擇已儲存的超商門市"
                  size={isMobile ? 'large' : 'middle'}
                  onChange={(id) => applyCvsAddress(savedCvs.find((a) => a.id === id))}
                  options={savedCvs.map((a) => ({
                    value: a.id,
                    label: `${a.is_default ? '★ ' : ''}${a.cvs_store_name}／${a.recipient_name}`,
                  }))}
                />
              </div>
            )}
            {cvsStore ? (
              <Alert
                type="success" showIcon
                message={`已選門市：${cvsStore.cvs_store_name}`}
                description={cvsStore.cvs_store_address}
                action={<Button size="small" onClick={pickCvsStore}>更換門市</Button>}
              />
            ) : (
              <Button block icon={<ShopOutlined />} onClick={pickCvsStore}
                style={isMobile ? { height: 44, fontSize: 16 } : undefined}>
                選擇取貨門市（綠界電子地圖）
              </Button>
            )}
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>取件人姓名</Text>
              <Input placeholder="請輸入取件人姓名" value={cvsName}
                onChange={(e) => setCvsName(e.target.value)}
                style={isMobile ? { height: 44, fontSize: 16 } : undefined} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>取件人電話</Text>
              <Input placeholder="請輸入取件人電話" inputMode="tel" value={cvsPhone}
                onChange={(e) => setCvsPhone(e.target.value)}
                style={isMobile ? { height: 44, fontSize: 16 } : undefined} />
            </div>
            {!cvsStore && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                未先選門市也可結帳，付款頁會再開啟電子地圖選店。
              </Text>
            )}
          </Space>
        </div>
      )}
    </Card>
  );

  // 優惠碼
  const couponCard = (
    <Card size="small" style={{ marginTop: 0, marginBottom: 12, borderRadius: 14, border: `1px solid ${BORDER}` }} styles={{ body: { padding: 16 } }}>
      <Text strong style={{ display: 'block', marginBottom: 8, color: HEADING }}><TagOutlined /> 優惠碼</Text>
      {myCoupons.length > 0 && (
        <Select
          style={{ width: '100%', marginBottom: 8 }}
          placeholder="從我的優惠券挑選"
          value={undefined}
          onChange={(val) => applyCoupon(val)}
          size={isMobile ? 'large' : 'middle'}
          options={myCoupons.map((c) => {
            const reason = couponDisabledReason(c);
            return {
              value: c.code,
              disabled: !!reason,
              label: `${couponLabelText(c)}（${c.code}）${reason ? ` — ${reason}` : ''}`,
            };
          })}
        />
      )}
      <Space.Compact style={{ width: '100%' }}>
        <Input
          placeholder="或直接輸入優惠碼"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onPressEnter={() => applyCoupon()}
          style={isMobile ? { height: 44, fontSize: 16 } : undefined}
        />
        <Button type="primary" loading={applying} onClick={() => applyCoupon()} style={isMobile ? { height: 44, fontSize: 16 } : undefined}>套用</Button>
      </Space.Compact>
      {coupon && coupon.valid && (
        <Alert
          style={{ marginTop: 10 }}
          type="success"
          showIcon
          message={<span>已套用 <b>{coupon.code || '優惠碼'}</b>{coupon.label ? `（${coupon.label}）` : (coupon.description ? `：${coupon.description}` : '')}　<Tag color="green">-NT$ {discount}</Tag></span>}
        />
      )}
      {coupon && !coupon.valid && (
        <Alert style={{ marginTop: 10 }} type="warning" showIcon message={coupon.reason || '優惠碼無法使用'} />
      )}
    </Card>
  );

  // 金額摘要 + 結帳按鈕（桌機置右並黏著）
  const summaryCard = (
    <Card
      size="small"
      style={{ borderRadius: 16, border: `1px solid ${BORDER}`, background: PANEL, boxShadow: '0 8px 24px rgba(34,41,87,0.08)' }}
      styles={{ body: { padding: 18 } }}
    >
      <Text strong style={{ display: 'block', marginBottom: 12, color: HEADING, fontSize: 16 }}>訂單摘要</Text>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text type="secondary">商品小計</Text>
        <Text>NT$ {subtotal}</Text>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text type="secondary">運費（{isHome ? '宅配到府' : '超商取貨'}）</Text>
        <Text>NT$ {shipping}</Text>
      </div>
      {discount > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
          <Text type="secondary">
            優惠折抵
            {coupon?.code ? (
              <Text style={{ color: BRAND, fontSize: 12 }}>（{coupon.code}{coupon.label ? `・${coupon.label}` : ''}）</Text>
            ) : null}
          </Text>
          <Text style={{ color: '#cf1322', whiteSpace: 'nowrap' }}>- NT$ {discount}</Text>
        </div>
      )}
      <Divider style={{ margin: '10px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Text strong>應付金額</Text>
        <Text strong style={{ color: BRAND, fontSize: 24 }}>NT$ {finalAmount}</Text>
      </div>
      <Button
        type="primary"
        size="large"
        block
        loading={checkingOut}
        onClick={handleCheckout}
        disabled={items.length === 0}
        style={{ height: 50, fontSize: 16, marginTop: 16, borderRadius: 12 }}
      >
        結帳
      </Button>
    </Card>
  );

  return (
    <div style={containerStyle}>
      <style>{`
        .cart-summary-sticky { position: sticky; top: 24px; }
        @media (hover: hover) {
          .cart-line-card { transition: transform .18s ease, box-shadow .18s ease; }
          .cart-line-card:hover { transform: translateY(-4px); box-shadow: 0 10px 24px rgba(34,41,87,0.16); }
        }
      `}</style>

      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/shop')} style={{ marginBottom: 12, paddingLeft: 0 }}>
        繼續購物
      </Button>
      <Title level={isMobile ? 3 : 2} style={{ marginTop: 0, color: BRAND, fontWeight: 800, letterSpacing: 1 }}>購物車</Title>

      {items.length === 0 ? (
        <Empty description="購物車是空的" style={{ padding: '48px 0' }}>
          <Link to="/shop"><Button type="primary">去逛逛商品</Button></Link>
        </Empty>
      ) : isMobile ? (
        // 手機：單欄堆疊
        <>
          {items.map((it) => (it.is_custom ? renderCustomItem(it) : renderPlainItem(it)))}
          {shippingSelector}
          {couponCard}
          {summaryCard}
          <div style={{ height: 24 }} />
        </>
      ) : (
        // 桌機：左品項清單(~62%) + 右黏著訂單摘要(360px)
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 32, alignItems: 'start' }}>
          <div>
            {items.map((it) => (it.is_custom ? renderCustomItem(it) : renderPlainItem(it)))}
            {shippingSelector}
            {couponCard}
          </div>
          <div className="cart-summary-sticky">
            {summaryCard}
          </div>
        </div>
      )}
    </div>
  );
}
